import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import styles from './AdminConfigTractorBooms.module.css';
import AddBoomDialog from './AddBoomDialog/AddBoomDialog';
import DeleteBoomDialog from './DeleteBoomDialog/DeleteBoomDialog';

const AdminConfigTractorBooms = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tractor, setTractor] = useState({});
  const [booms, setBooms] = useState([]);
  const [fetchedBooms, setFetchedBooms] = useState([]);
  const [boomForDeletion, setBoomForDeletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(false);
  const [addBoomDialogOpen, setIsAddBoomDialogOpen] = useState(false);
  const [deleteBoomDialogOpen, setIsDeleteBoomDialogOpen] = useState(false);

  const getClientId = useCallback(
    () => searchParams.get('clientId'),
    [searchParams]
  );

  const getTractorId = useCallback(
    () => searchParams.get('tractorId'),
    [searchParams]
  );

  const fetchTractor = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractor?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const tractor = await response.json();
      setTractor(tractor);
      setLoading(false);
      setError(false);
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
    }
  }, [getClientId, getTractorId]);

  const fetchBooms = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = getClientId();
      const tractorId = getTractorId();
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/booms?clientId=${clientId}&tractorId=${tractorId}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const booms = (await response.json()).map((n) => ({
        ...n,
        overlap: Math.round(n.overlap * 100)
      }));
      setFetchedBooms(booms);
      setBooms(booms);
      setLoading(false);
      setError(false);
    } catch (error) {
      setTimeout(() => {
        setError(true);
        setLoading(false);
      }, 1000);
    }
  }, [getClientId, getTractorId]);

  useEffect(() => {
    fetchTractor();
    fetchBooms();
  }, [getClientId, getTractorId, fetchTractor, fetchBooms]);

  const onEditBoom = (boomId, fieldName, newValue) => {
    const updatedBooms = booms.map((boom) => {
      if (boom.id === boomId) {
        return {
          ...boom,
          [fieldName]: newValue
        };
      }
      return boom;
    });
    setBooms(updatedBooms);
  };

  const hasBoomValueChanged = (boom, fieldName) => {
    const fetchedBoom = fetchedBooms.find(
      (fetchedBoom) => fetchedBoom.id === boom.id
    );
    return fetchedBoom[fieldName] !== boom[fieldName];
  };

  const haveAnyValuesChanged = () => {
    return _.isEqual(fetchedBooms, booms);
  };

  const discardChanges = () => {
    setBooms(fetchedBooms);
  };

  const updateBooms = async () => {
    const token = await authService.getAccessToken();
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/booms`,
      {
        method: 'POST',
        headers: !token
          ? {}
          : {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        body: JSON.stringify(booms)
      }
    );

    if (response.ok) {
      toast.success('Boom updated successfully!');
    } else {
      toast.error(`${response.status} ${response.statusText}`);
    }
    fetchBooms();
  };

  const openAddBoomDialog = () => {
    setIsAddBoomDialogOpen(true);
  };

  const closeShowAddBoomDialog = () => {
    setIsAddBoomDialogOpen(false);
  };

  const handleAddBoomSuccess = () => {
    setIsAddBoomDialogOpen(false);
    fetchBooms();
  };

  const openDeleteBoomDialog = (boom) => {
    setBoomForDeletion(boom);
    setIsDeleteBoomDialogOpen(true);
  };

  const closeDeleteBoomDialog = () => {
    setBoomForDeletion(null);
    setIsDeleteBoomDialogOpen(false);
  };

  const handleDeleteBoomSuccess = () => {
    closeDeleteBoomDialog();
    fetchBooms();
  };

  // Mobile Card View
  const renderMobileView = () => (
    <Stack spacing={2} sx={{ mt: 2 }}>
      {booms.map((boom, index) => (
        <Card key={boom.id} variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Boom #{index + 1}
              </Typography>

              <TextField
                label="Boom Name"
                type="text"
                fullWidth
                value={boom.name}
                error={hasBoomValueChanged(boom, 'name')}
                onChange={(e) => onEditBoom(boom.id, 'name', e.target.value)}
              />

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={boom.isActive}
                  onChange={(e) => onEditBoom(boom.id, 'isActive', e.target.checked)}
                />
                <Typography>Is Active</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Nozzle Count: {boom.nozzleCount}
                </Typography>
              </Box>

              <TextField
                label="Switch Number"
                type="number"
                fullWidth
                InputProps={{ inputProps: { step: 'any' } }}
                value={boom.switchNumber}
                error={hasBoomValueChanged(boom, 'switchNumber')}
                onChange={(e) => onEditBoom(boom.id, 'switchNumber', e.target.value)}
              />

              <TextField
                label="Notes"
                type="text"
                fullWidth
                multiline
                rows={3}
                value={boom.notes}
                error={hasBoomValueChanged(boom, 'notes')}
                onChange={(e) => onEditBoom(boom.id, 'notes', e.target.value)}
              />

              <Button
                variant="outlined"
                fullWidth
                startIcon={<DeleteIcon />}
                disabled={!haveAnyValuesChanged()}
                onClick={() => openDeleteBoomDialog(boom)}
              >
                Delete Boom
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  // Desktop Table View
  const renderDesktopView = () => (
    <TableContainer component={Paper} sx={{ my: 1 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Boom Name</TableCell>
            <TableCell>Is Active</TableCell>
            <TableCell>Nozzle Count</TableCell>
            <TableCell>Switch Number</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {booms.map((boom, index) => (
            <TableRow key={boom.id}>
              <TableCell>{index + 1}</TableCell>

              <TableCell>
                <TextField
                  type="text"
                  InputProps={{ inputProps: { step: 'any' } }}
                  value={boom.name}
                  error={hasBoomValueChanged(boom, 'name')}
                  onChange={(e) => onEditBoom(boom.id, 'name', e.target.value)}
                />
              </TableCell>

              <TableCell>
                <Checkbox
                  checked={boom.isActive}
                  onChange={(e) => onEditBoom(boom.id, 'isActive', e.target.checked)}
                />
              </TableCell>

              <TableCell>
                <Typography>{boom.nozzleCount}</Typography>
              </TableCell>

              <TableCell>
                <TextField
                  type="number"
                  InputProps={{ inputProps: { step: 'any' } }}
                  value={boom.switchNumber}
                  error={hasBoomValueChanged(boom, 'switchNumber')}
                  onChange={(e) => onEditBoom(boom.id, 'switchNumber', e.target.value)}
                />
              </TableCell>

              <TableCell>
                <TextField
                  type="text"
                  multiline
                  rows={4}
                  InputProps={{ inputProps: { step: 'any' } }}
                  value={boom.notes}
                  error={hasBoomValueChanged(boom, 'notes')}
                  onChange={(e) => onEditBoom(boom.id, 'notes', e.target.value)}
                />
              </TableCell>

              <TableCell>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  disabled={!haveAnyValuesChanged()}
                  onClick={() => openDeleteBoomDialog(boom)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
        <Typography variant="h4" sx={{ pt: { xs: 2, sm: 0 }, pb: 1 }}>
          {tractor.name}
        </Typography>
        <Typography variant="h5" sx={{ pb: 2 }}>
          Boom Management
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography>
              Displays all the booms on the sprayer.
            </Typography>
          </Box>
          {haveAnyValuesChanged() ? (
            <Button
              variant="outlined"
              disabled={loading}
              startIcon={<AddIcon />}
              onClick={openAddBoomDialog}
              fullWidth={isMobile}
            >
              Add Boom
            </Button>
          ) : (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="outlined"
                startIcon={<UndoIcon />}
                onClick={discardChanges}
                fullWidth={isMobile}
              >
                Discard Changes
              </Button>
              <Button
                variant="outlined"
                onClick={updateBooms}
                startIcon={<SaveIcon />}
                fullWidth={isMobile}
              >
                Save Changes
              </Button>
            </Stack>
          )}
        </Stack>

        {isMobile ? renderMobileView() : renderDesktopView()}

        {addBoomDialogOpen && (
          <AddBoomDialog
            clientId={getClientId()}
            tractorId={getTractorId()}
            open={addBoomDialogOpen}
            onClose={closeShowAddBoomDialog}
            onSuccess={handleAddBoomSuccess}
          />
        )}
        {deleteBoomDialogOpen && (
          <DeleteBoomDialog
            clientId={getClientId()}
            boomId={boomForDeletion.id}
            open={deleteBoomDialogOpen}
            onClose={closeDeleteBoomDialog}
            onSuccess={handleDeleteBoomSuccess}
          />
        )}
      </Box>
    </>
  );
};

export default AdminConfigTractorBooms;